import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { RubrikConfigEntry } from '../../../core/base-api';
import { AppCustomConfigService } from '../../../core/services/app-custom-config.service';
import { RubrikComponentVM, RubrikVMService } from './rubrik-v-m.service';
import { ViewMode } from '../../../ech0134/meldungsempfaenger/view-mode.enum';
import { DirtyCheckModalComponent } from '../../../shared/components/modals/dirty-check-modal/dirty-check-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CapAlertService } from '../../../core/extension/exceptions/cap-alert.service';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { RubrikConfigService } from './rubrik-config.service';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { RubrikComponent } from '../../../shared/components/rubrik/rubrik.component';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import RubrikEnum = RubrikConfigEntry.RubrikEnum;

@Component({
  selector: 'cap-rubrik-config',
  templateUrl: './rubrik-config.component.html',
  styleUrls: ['./rubrik-config.component.scss'],
  imports: [RubrikComponent, TranslatePipe, NgTemplateOutlet, CdkDropList, CdkDrag, NgClass, NgStyle]
})
export class RubrikConfigComponent implements OnInit, OnDestroy {
  availableRubrikComponents: Array<string>;
  model: RubrikConfigEntry;
  componentsVMArray: Array<RubrikComponentVM> = [];

  private subscription = new Subscription();
  private viewMode: ViewMode;

  rubrikTitle: string;
  rubrikType: RubrikEnum;

  public constructor(
    protected rubrikService: AppCustomConfigService,
    protected rubrikVmService: RubrikVMService,
    protected modalService: NgbModal,
    protected capAlertService: CapAlertService,
    protected translate: TranslateService,
    protected route: ActivatedRoute
  ) {
    keysToTranslate<string>([
      'Anzeige',
      'Rubrik',
      'auszugAmtlicheBewertungComponent',
      'auszugAnmerkungenComponent',
      'auszugDienstbarkeitenComponent',
      'auszugDienstbarkeitenPlaceHolderComponent',
      'auszugDominierteGrundstueckeComponent',
      'auszugEigentumComponent',
      'auszugGemeindeGrundstueckComponent',
      'auszugGrundbuchUebersichtComponent',
      'auszugGrundbuchUebersichtMiteigentumComponent',
      'auszugGrundbuchUebersichtStockwerkComponent',
      'auszugGrundbuchVertragstexteComponent',
      'auszugGrundlastenComponent',
      'auszugGrundstueckBeschreibungLiegenschaftComponent',
      'auszugGrundstueckBeschreibungMiteigentumComponent',
      'auszugGrundstueckBeschreibungSDRComponent',
      'auszugGrundstueckBeschreibungStockwerkComponent',
      'auszugGwrComponent',
      'auszugHaengigeGeschaefteGrundbuchComponent',
      'auszugOerebComponent',
      'auszugOerebPlaceHolderComponent',
      'auszugPfandrechteComponent',
      'auszugRangregelungenComponent',
      'auszugSeybuchAlpgrundstueckeComponent',
      'auszugSeybuchGrundbuchDaten',
      'auszugSeybuchGrundstueckBeschreibung',
      'auszugVermessungUebersichtLiegenschaftComponent',
      'auszugVermessungUebersichtSDRComponent',
      'auszugVormerkungenComponent',
      'detailGrundstueckAnmerkungen',
      'detailGrundstueckBeschreibung',
      'detailGrundstueckDienstbarkeiten',
      'detailGrundstueckEigentum',
      'detailGrundstueckGeschaefteAuftraege',
      'detailGrundstueckGrundlasten',
      'detailGrundstueckGrundpfandrechte',
      'detailGrundstueckRangregelungen',
      'detailGrundstueckVormerkungen',
      'detailPfandrechtBelastungen',
      'detailPfandrechtHaengigeGeschaefte',
      'detailPfandrechtKopfdaten',
      'detailPfandrechtPerimeter',
      'detailPfandrechtPfandtitel',
      'detailPfandrechtSchuldner',
      'detailStandardrechtBelastungen',
      'detailStandardrechtHaengigeGeschaefte',
      'detailStandardrechtKopfdaten',
      'detailStandardrechtPerimeter',
      'uebersichtAmtlicheBewertungComponent',
      'uebersichtOerebPlaceHolderComponent'
    ]);
    keysToTranslate<RubrikEnum>([
      'GB_AUSZUG_LIG',
      'GB_AUSZUG_MIT',
      'GB_AUSZUG_SDR',
      'GB_AUSZUG_SEYBUCH',
      'GB_AUSZUG_STW',
      'GB_DETAIL_PFANDRECHT',
      'GB_DETAIL_STANDARDRECHT',
      'GB_UEBERSICHT_LIG',
      'GB_UEBERSICHT_MIT',
      'GB_UEBERSICHT_SDR',
      'GB_UEBERSICHT_STW'
    ]);
  }

  ngOnInit(): void {
    this.viewMode = ViewMode.READ_ONLY;
    const results = RubrikConfigService.getConfigsFrom(this.route, this.rubrikType);
    this.model = results[0];
    this.availableRubrikComponents = results[1];
    this.componentsVMArray = this.rubrikVmService.modelToVM(this.model.components, this.availableRubrikComponents);
  }

  isEditMode(): boolean {
    return this.viewMode === ViewMode.EDIT;
  }

  isNewMode(): boolean {
    return this.viewMode === ViewMode.NEW;
  }

  isReadOnlyMode(): boolean {
    return this.viewMode === ViewMode.READ_ONLY;
  }

  onEdit() {
    this.viewMode = ViewMode.EDIT;
  }

  get visibleComponents() {
    return this.componentsVMArray.filter((a) => a.visible);
  }

  get invisibleComponents() {
    return this.componentsVMArray.filter((a) => !a.visible);
  }

  onSaveComponents() {
    if (this.model && this.visibleComponents) {
      this.model.components = this.rubrikVmService.VMToModel(this.visibleComponents);
      this.subscription.add(
        this.rubrikService.updateRubrikConfig(this.model).subscribe(() => {
          this.capAlertService.success(this.translate.instant('saveSuccessMessage'));
        })
      );
    }
    if (this.visibleComponents.length === 0) {
      this.resetModel();
    }
    this.viewMode = ViewMode.READ_ONLY;
  }

  onCancelComponents() {
    if (this.viewMode === ViewMode.EDIT && this.checkDirty()) {
      this.modalService.open(DirtyCheckModalComponent).result.then(
        () => {
          this.resetModel();
        },
        () => {}
      );
      return;
    }
    this.resetModel();
  }

  checkDirty(): boolean {
    const arrVM = this.rubrikVmService.VMToModel(this.visibleComponents);
    const arrM = this.model.components;
    if (arrVM.length !== arrM.length) {
      return true;
    }
    for (const value of arrVM) {
      const indexVM = arrVM.indexOf(value);
      const indexM = arrM.indexOf(value);
      if (indexVM !== indexM) {
        return true;
      }
    }
    return false;
  }

  drop(event: CdkDragDrop<RubrikComponentVM[]>) {
    if (this.visibleComponents.length === 0 && event.previousIndex !== event.currentIndex) {
      this.componentsVMArray.forEach((a) => (a.visible = true));
    }
    moveItemInArray(this.componentsVMArray, event.previousIndex, event.currentIndex);
  }

  protected resetModel() {
    this.componentsVMArray = this.rubrikVmService.modelToVM(this.model.components, this.availableRubrikComponents);
    this.viewMode = ViewMode.READ_ONLY;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
